import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate, createFileRoute } from "@tanstack/react-router";
import { FixedLayout } from "@/components/shared/fixed-layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Field, FieldError, FieldLabel } from "@/components/ui/field";
import { phoneNumberRegex } from "@/lib/regex";
import { usePostSendSms, usePostVerifySms } from "@/lib/api/sms/hooks";
import { useAlertDialogStore } from "@/lib/store/use-alert-dialog-store";
import { useLoadingIndicator } from "@/lib/hooks/use-loading-indicator";
import { apiClient } from "@/lib/api/client";
import { AxiosError } from "axios";

const FIND_PASSWORD_TITLE = "비밀번호 재설정";

const PLACEHOLDER = {
  USER_ID: "아이디를 입력해주세요",
  NAME: "이름을 입력해주세요",
  PHONE_NUMBER: "01012345678",
  VERIFICATION_CODE: "인증번호 6자리를 입력해 주세요.",
  NEW_PASSWORD: "새 비밀번호를 입력해주세요",
  CONFIRM_PASSWORD: "비밀번호를 다시 입력해주세요",
};

const ERROR_MESSAGE = {
  USER_ID_REQUIRED: "아이디를 입력해주세요.",
  NAME_REQUIRED: "이름을 입력해주세요.",
  PHONE_NUMBER_REQUIRED: "휴대전화 번호를 입력해주세요.",
  PHONE_NUMBER_FORMAT:
    "휴대전화 번호는 010으로 시작하는 11자리 숫자여야 합니다.",
  VERIFICATION_CODE_REQUIRED: "인증번호를 입력해주세요.",
  VERIFICATION_CODE_FORMAT: "인증번호는 6자리 숫자여야 합니다.",
  PASSWORD_REQUIRED: "비밀번호를 입력해주세요.",
  PASSWORD_MIN: "비밀번호는 최소 8자 이상이어야 합니다.",
  PASSWORD_FORMAT: "비밀번호는 영문, 숫자, 특수문자를 포함해야 합니다.",
  PASSWORD_MATCH: "비밀번호가 일치하지 않습니다.",
};

const VERIFICATION_TIME_LIMIT = 300; // 5분

const passwordRegex =
  /^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*#?&])[A-Za-z\d@$!%*#?&]{8,}$/;

// 인증 단계 스키마
const verificationSchema = z.object({
  userId: z.string().min(1, ERROR_MESSAGE.USER_ID_REQUIRED),
  name: z.string().min(1, ERROR_MESSAGE.NAME_REQUIRED),
  phoneNumber: z
    .string()
    .min(1, ERROR_MESSAGE.PHONE_NUMBER_REQUIRED)
    .regex(phoneNumberRegex, ERROR_MESSAGE.PHONE_NUMBER_FORMAT),
  verificationCode: z
    .string()
    .min(1, ERROR_MESSAGE.VERIFICATION_CODE_REQUIRED)
    .regex(/^[0-9]{6}$/, ERROR_MESSAGE.VERIFICATION_CODE_FORMAT),
  newPassword: z.string().optional(),
  confirmPassword: z.string().optional(),
});

// 비밀번호 재설정 단계 스키마
const newPasswordSchema = z
  .object({
    userId: z.string(),
    name: z.string(),
    phoneNumber: z.string(),
    verificationCode: z.string(),
    newPassword: z
      .string()
      .min(1, ERROR_MESSAGE.PASSWORD_REQUIRED)
      .min(8, ERROR_MESSAGE.PASSWORD_MIN)
      .regex(passwordRegex, ERROR_MESSAGE.PASSWORD_FORMAT),
    confirmPassword: z.string().min(1, ERROR_MESSAGE.PASSWORD_REQUIRED),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: ERROR_MESSAGE.PASSWORD_MATCH,
    path: ["confirmPassword"],
  });

type ResetPasswordFormData = {
  userId: string;
  name: string;
  phoneNumber: string;
  verificationCode: string;
  newPassword?: string;
  confirmPassword?: string;
};

function FindPasswordPage() {
  const navigate = useNavigate();
  const { setAlertDialogState, resetAlertDialogState } = useAlertDialogStore();

  const [step, setStep] = useState<"verification" | "newPassword">(
    "verification"
  );
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [remainingTime, setRemainingTime] = useState(VERIFICATION_TIME_LIMIT);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [isVerified, setIsVerified] = useState(false);

  const { mutateAsync: sendSms, isPending: isSending } = usePostSendSms();
  const { mutateAsync: verifySms, isPending: isVerifying } = usePostVerifySms();

  const [isResetting, setIsResetting] = useState(false);

  useLoadingIndicator(isSending || isVerifying || isResetting);

  const {
    control,
    handleSubmit,
    formState: { isValid },
    trigger,
    getValues,
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(
      step === "verification" ? verificationSchema : newPasswordSchema
    ),
    mode: "onChange",
    defaultValues: {
      userId: "",
      name: "",
      phoneNumber: "",
      verificationCode: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleSendVerification = async () => {
    const phone = getValues("phoneNumber");
    const name = getValues("name");
    const userId = getValues("userId");

    if (!userId || !name || !phone) return;

    try {
      await sendSms({ phoneNumber: phone, name });
      setIsVerificationSent(true);
      setRemainingTime(VERIFICATION_TIME_LIMIT);
      setCooldownTime(60);

      setAlertDialogState({
        title: "인증번호 전송",
        description: "입력하신 휴대전화 번호로 인증번호를 전송했습니다.",
        onConfirm: () => {
          resetAlertDialogState();
        },
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        setAlertDialogState({
          title: "인증번호 전송 실패",
          description:
            error.response?.data?.message ||
            "인증번호 전송에 실패했습니다. 아이디, 이름, 휴대전화 번호를 확인해주세요.",
          onConfirm: () => {
            resetAlertDialogState();
          },
        });
      }
    }
  };

  const handleVerification = async (data: ResetPasswordFormData) => {
    try {
      console.log("인증 시작:", data);

      // SMS 인증 확인
      await verifySms({
        phoneNumber: data.phoneNumber,
        code: data.verificationCode!,
        name: data.name,
      });
      console.log("SMS 인증 성공");

      // 사용자 확인 (아이디, 이름, 전화번호 일치 확인)
      await apiClient.post("/auth/verify-user", {
        userId: data.userId,
        name: data.name,
        phoneNumber: data.phoneNumber,
      });
      console.log("사용자 확인 성공");

      setIsVerified(true);
      setStep("newPassword");
      console.log("step 변경됨: newPassword");

      setAlertDialogState({
        title: "인증 완료",
        description: "본인 인증이 완료되었습니다. 새 비밀번호를 입력해주세요.",
        onConfirm: () => {
          resetAlertDialogState();
        },
      });
    } catch (error: unknown) {
      console.error("인증 실패:", error);
      if (error instanceof AxiosError) {
        setAlertDialogState({
          title: "인증 실패",
          description:
            error.response?.data?.message ||
            "일치하는 회원 정보를 찾을 수 없습니다.",
          onConfirm: () => {
            resetAlertDialogState();
          },
        });
      }
    }
  };

  const handleResetPassword = async (data: ResetPasswordFormData) => {
    try {
      setIsResetting(true);

      await apiClient.post("/auth/reset-password", {
        userId: data.userId,
        newPassword: data.newPassword,
      });

      setAlertDialogState({
        title: "비밀번호 재설정 완료",
        description: "비밀번호가 성공적으로 변경되었습니다. 로그인해주세요.",
        onConfirm: () => {
          navigate({ to: "/auth/login" });
          resetAlertDialogState();
        },
      });
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        setAlertDialogState({
          title: "비밀번호 재설정 실패",
          description:
            error.response?.data?.message ||
            "비밀번호 재설정에 실패했습니다. 다시 시도해주세요.",
          onConfirm: () => {
            resetAlertDialogState();
          },
        });
      }
    } finally {
      setIsResetting(false);
    }
  };

  const onSubmit = async (data: ResetPasswordFormData) => {
    if (step === "verification") {
      await handleVerification(data);
    } else {
      await handleResetPassword(data);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const handlePhoneNumberChange = (
    value: string,
    onChange: (...event: unknown[]) => void
  ) => {
    const digitsOnly = value.replace(/[^0-9]/g, "");
    onChange(digitsOnly);
  };

  // 인증번호 타이머
  useEffect(() => {
    if (isVerificationSent && remainingTime > 0 && !isVerified) {
      const timer = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isVerificationSent, remainingTime, isVerified]);

  // 쿨다운 타이머
  useEffect(() => {
    if (cooldownTime > 0) {
      const timer = setInterval(() => {
        setCooldownTime((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [cooldownTime]);

  return (
    <FixedLayout
      headerType="back"
      title={FIND_PASSWORD_TITLE}
      showButton={
        step === "verification" ? isVerificationSent : step === "newPassword"
      }
      buttonText={step === "verification" ? "다음" : "비밀번호 재설정"}
      buttonDisabled={
        step === "verification"
          ? !isValid || remainingTime === 0 || isVerifying
          : !isValid || isResetting
      }
      onSubmit={handleSubmit(onSubmit)}
    >
      <form id="reset-password-form" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-6">
          {step === "verification" && (
            <>
              {/* 아이디 입력 */}
              <Controller
                name="userId"
                control={control}
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel htmlFor="reset-password-user-id">
                      아이디
                    </FieldLabel>
                    <Input
                      {...field}
                      id="reset-password-user-id"
                      type="text"
                      placeholder={PLACEHOLDER.USER_ID}
                      aria-invalid={fieldState.invalid}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />

              {/* 이름 입력 */}
              <Controller
                name="name"
                control={control}
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel htmlFor="reset-password-name">이름</FieldLabel>
                    <Input
                      {...field}
                      id="reset-password-name"
                      type="text"
                      placeholder={PLACEHOLDER.NAME}
                      aria-invalid={fieldState.invalid}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />

              {/* 휴대전화 번호 입력 */}
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel htmlFor="reset-password-phone-number">
                      휴대전화 번호
                    </FieldLabel>
                    <div className="w-full flex gap-2">
                      <Input
                        {...field}
                        id="reset-password-phone-number"
                        type="tel"
                        placeholder={PLACEHOLDER.PHONE_NUMBER}
                        aria-invalid={fieldState.invalid}
                        onChange={(e) =>
                          handlePhoneNumberChange(
                            e.target.value,
                            field.onChange
                          )
                        }
                        wrapperClassName="flex-1"
                      />
                      <Button
                        type="button"
                        onClick={async () => {
                          const isUserIdValid = await trigger("userId");
                          const isNameValid = await trigger("name");
                          const isPhoneValid = await trigger("phoneNumber");
                          if (isUserIdValid && isNameValid && isPhoneValid) {
                            await handleSendVerification();
                          }
                        }}
                        disabled={
                          !field.value ||
                          fieldState.invalid ||
                          isSending ||
                          cooldownTime > 0
                        }
                      >
                        {cooldownTime > 0
                          ? `${cooldownTime}초 후 재발송`
                          : isVerificationSent
                            ? "인증번호 재발송"
                            : "인증번호 받기"}
                      </Button>
                    </div>
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />

              {/* 인증번호 입력 */}
              {isVerificationSent && (
                <Controller
                  name="verificationCode"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Field>
                      <div className="flex justify-between items-center">
                        <FieldLabel htmlFor="reset-password-verification-code">
                          인증번호
                        </FieldLabel>
                        <span
                          className={`text-sm ${
                            remainingTime > 0 ? "text-gray-600" : "text-red-500"
                          }`}
                        >
                          {remainingTime > 0
                            ? formatTime(remainingTime)
                            : "시간 만료"}
                        </span>
                      </div>
                      <Input
                        {...field}
                        id="reset-password-verification-code"
                        type="text"
                        inputMode="numeric"
                        placeholder={PLACEHOLDER.VERIFICATION_CODE}
                        aria-invalid={fieldState.invalid}
                        maxLength={6}
                        onChange={(e) => {
                          const value = e.target.value.replace(/[^0-9]/g, "");
                          field.onChange(value);
                        }}
                      />
                      {fieldState.invalid && (
                        <FieldError errors={[fieldState.error]} />
                      )}
                    </Field>
                  )}
                />
              )}
            </>
          )}

          {step === "newPassword" && (
            <>
              {/* 새 비밀번호 입력 */}
              <Controller
                name="newPassword"
                control={control}
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel htmlFor="reset-password-new-password">
                      새 비밀번호
                    </FieldLabel>
                    <Input
                      {...field}
                      id="reset-password-new-password"
                      type="password"
                      placeholder={PLACEHOLDER.NEW_PASSWORD}
                      aria-invalid={fieldState.invalid}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />

              {/* 비밀번호 확인 */}
              <Controller
                name="confirmPassword"
                control={control}
                render={({ field, fieldState }) => (
                  <Field>
                    <FieldLabel htmlFor="reset-password-confirm-password">
                      비밀번호 확인
                    </FieldLabel>
                    <Input
                      {...field}
                      id="reset-password-confirm-password"
                      type="password"
                      placeholder={PLACEHOLDER.CONFIRM_PASSWORD}
                      aria-invalid={fieldState.invalid}
                    />
                    {fieldState.invalid && (
                      <FieldError errors={[fieldState.error]} />
                    )}
                  </Field>
                )}
              />
            </>
          )}
        </div>
      </form>
    </FixedLayout>
  );
}

export const Route = createFileRoute("/auth/find-password")({
  component: FindPasswordPage,
});
